import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Slider,
  Stack,
  IconButton,
  Tooltip,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Speed as SpeedIcon,
  MyLocation as ZoomIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  TravelExplore as PlaceLookupIcon,
} from '@mui/icons-material';
import EmptyState from '@signals/app-shell/components/common/EmptyState';
import { hasReverseGeocodeProvider, reverseGeocode } from '../../utils/reverseGeocode';

interface DwellAnalysisProps {
  selectedStudents: string[];
  selectedCourse?: string;
  dateRange: { start: Date | null; end: Date | null };
  dwellLocations: any[]; // Pre-loaded dwell data from parent
  onDwellClick?: (dwell: DwellData | null) => void; // Callback when dwell is clicked/unclicked
  /** Persist a resolved place name onto matching dwells (map labels + reload). */
  onPlaceNameResolved?: (lat: number, lon: number, placeName: string, placeType?: string) => void;
}

interface DwellData {
  id: number;
  device_reference_id: number;
  entity_id: string;
  device_id: string;
  device_type: string;
  course_id: string;
  latitude: number;
  longitude: number;
  duration: string;
  duration_seconds: number;
  start_time: string;
  end_time: string;
  place_name?: string;  // Geocoded place name
  place_type?: string;  // Place category (e.g., cafe, university, etc.)
}

interface DwellLocationGroup {
  key: string;
  location_name: string;
  latitude: number;
  longitude: number;
  place_type: string | null;
  dwells: DwellData[];
  total_duration: number;
  avg_duration: number;
  entities: string[];
  device_types: string[];
  first_seen: string;
  last_seen: string;
}

const DEVICE_COLORS: Record<string, 'success' | 'info' | 'secondary' | 'default'> = {
  phone: 'success',
  watch: 'info',
  vehicle: 'secondary',
};

const formatDuration = (seconds: number) => {
  if (!seconds || seconds <= 0) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatDateShort = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
};

// Group dwells by approximate proximity so repeat visits to the same place
// collapse into a single card (matches the POL Dwell Analysis layout).
function groupDwellsByLocation(dwells: DwellData[]): DwellLocationGroup[] {
  const PROXIMITY_THRESHOLD = 0.001; // ~100m in degrees
  const groups: DwellLocationGroup[] = [];

  for (const dwell of dwells) {
    if (typeof dwell.latitude !== 'number' || typeof dwell.longitude !== 'number') continue;

    let matched = false;
    for (const group of groups) {
      const latDiff = Math.abs(group.latitude - dwell.latitude);
      const lonDiff = Math.abs(group.longitude - dwell.longitude);
      if (latDiff < PROXIMITY_THRESHOLD && lonDiff < PROXIMITY_THRESHOLD) {
        group.dwells.push(dwell);
        matched = true;
        break;
      }
    }

    if (!matched) {
      groups.push({
        key: `${dwell.latitude.toFixed(4)}_${dwell.longitude.toFixed(4)}`,
        location_name: dwell.place_name || `${dwell.latitude.toFixed(5)}, ${dwell.longitude.toFixed(5)}`,
        latitude: dwell.latitude,
        longitude: dwell.longitude,
        place_type: dwell.place_type ?? null,
        dwells: [dwell],
        total_duration: 0,
        avg_duration: 0,
        entities: [],
        device_types: [],
        first_seen: '',
        last_seen: '',
      });
    }
  }

  for (const group of groups) {
    group.total_duration = group.dwells.reduce((sum, d) => sum + d.duration_seconds, 0);
    group.avg_duration = group.dwells.length ? group.total_duration / group.dwells.length : 0;
    group.entities = Array.from(new Set(group.dwells.map((d) => d.entity_id)));
    group.device_types = Array.from(new Set(group.dwells.map((d) => d.device_type)));
    const times = group.dwells.map((d) => new Date(d.start_time).getTime()).sort((a, b) => a - b);
    group.first_seen = new Date(times[0]).toISOString();
    group.last_seen = new Date(times[times.length - 1]).toISOString();
  }

  groups.sort((a, b) => b.dwells.length - a.dwells.length);
  return groups;
}

const DURATION_MARKS = [
  { value: 0, label: '0' },
  { value: 15, label: '15m' },
  { value: 60, label: '1h' },
  { value: 240, label: '4h' },
  { value: 720, label: '12h' },
];

const DwellAnalysis: React.FC<DwellAnalysisProps> = ({
  selectedStudents,
  selectedCourse: _selectedCourse,
  dateRange: _dateRange,
  dwellLocations,
  onDwellClick,
  onPlaceNameResolved,
}) => {
  const [deviceFilter, setDeviceFilter] = useState<string>('all');
  const [minDurationMin, setMinDurationMin] = useState<number>(0);
  const [sortBy, setSortBy] = useState<'visits' | 'duration' | 'recent'>('visits');
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [selectedDwellId, setSelectedDwellId] = useState<number | null>(null);
  // On-demand reverse-geocode labels keyed by group.key (overrides coords title).
  const [resolvedNames, setResolvedNames] = useState<Record<string, string>>({});
  const [lookupLoading, setLookupLoading] = useState<Record<string, boolean>>({});
  const [lookupError, setLookupError] = useState<Record<string, string>>({});
  const [lookupAllRunning, setLookupAllRunning] = useState(false);
  const canLookupPlace = hasReverseGeocodeProvider();

  const uniqueDeviceTypes = useMemo<string[]>(
    () => Array.from(new Set(dwellLocations.map((d: DwellData) => d.device_type).filter(Boolean))),
    [dwellLocations],
  );

  // Apply student / device / duration filters before grouping.
  const filteredDwells = useMemo<DwellData[]>(() => {
    let list: DwellData[] = [...dwellLocations] as DwellData[];
    if (selectedStudents.length > 0) {
      list = list.filter((d) => selectedStudents.includes(d.entity_id));
    }
    if (deviceFilter !== 'all') {
      list = list.filter((d) => (d.device_type || '').toLowerCase() === deviceFilter);
    }
    if (minDurationMin > 0) {
      list = list.filter((d) => d.duration_seconds >= minDurationMin * 60);
    }
    return list;
  }, [dwellLocations, selectedStudents, deviceFilter, minDurationMin]);

  const groups = useMemo<DwellLocationGroup[]>(() => {
    const g = groupDwellsByLocation(filteredDwells);
    if (sortBy === 'duration') g.sort((a, b) => b.total_duration - a.total_duration);
    else if (sortBy === 'recent') g.sort((a, b) => new Date(b.last_seen).getTime() - new Date(a.last_seen).getTime());
    // 'visits' is the natural sort already applied inside groupDwellsByLocation
    return g;
  }, [filteredDwells, sortBy]);

  // Reset transient selection state whenever the underlying dataset changes
  // so we don't leave a stale highlight pointing at a dwell that's no longer
  // in view (e.g. after the user reloads with a new entity / date range).
  useEffect(() => {
    setSelectedDwellId(null);
    setExpandedGroup(null);
    setResolvedNames({});
    setLookupLoading({});
    setLookupError({});
    setLookupAllRunning(false);
  }, [dwellLocations]);

  const lookupPlaceForGroup = useCallback(
    async (group: DwellLocationGroup): Promise<boolean> => {
      if (!canLookupPlace) return false;
      setLookupLoading((prev) => ({ ...prev, [group.key]: true }));
      setLookupError((prev) => {
        const next = { ...prev };
        delete next[group.key];
        return next;
      });
      try {
        const result = await reverseGeocode(group.latitude, group.longitude);
        if (!result?.placeName) {
          setLookupError((prev) => ({ ...prev, [group.key]: 'No place found' }));
          return false;
        }
        setResolvedNames((prev) => ({ ...prev, [group.key]: result.placeName }));
        onPlaceNameResolved?.(group.latitude, group.longitude, result.placeName, result.placeType);
        return true;
      } catch {
        setLookupError((prev) => ({ ...prev, [group.key]: 'Lookup failed' }));
        return false;
      } finally {
        setLookupLoading((prev) => ({ ...prev, [group.key]: false }));
      }
    },
    [canLookupPlace, onPlaceNameResolved],
  );

  const handlePlaceLookup = useCallback(
    async (group: DwellLocationGroup, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!canLookupPlace || lookupLoading[group.key] || lookupAllRunning) return;
      await lookupPlaceForGroup(group);
    },
    [canLookupPlace, lookupLoading, lookupAllRunning, lookupPlaceForGroup],
  );

  const handleLookupAllPlaces = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!canLookupPlace || lookupAllRunning || groups.length === 0) return;
      setLookupAllRunning(true);
      try {
        for (const group of groups) {
          // Skip groups that already have a resolved / non-coordinate label.
          if (resolvedNames[group.key]) continue;
          const name = (group.location_name || '').trim();
          const looksLikeCoords = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(name)
            || name.toLowerCase().startsWith('dwell at');
          if (name && !looksLikeCoords) continue;
          await lookupPlaceForGroup(group);
          await new Promise((r) => setTimeout(r, 80));
        }
      } finally {
        setLookupAllRunning(false);
      }
    },
    [canLookupPlace, lookupAllRunning, groups, resolvedNames, lookupPlaceForGroup],
  );

  const stats = useMemo(() => {
    let totalDuration = 0;
    let longest = 0;
    filteredDwells.forEach((d) => {
      totalDuration += d.duration_seconds;
      if (d.duration_seconds > longest) longest = d.duration_seconds;
    });
    return {
      count: filteredDwells.length,
      locations: groups.length,
      totalDuration,
      avg: filteredDwells.length ? totalDuration / filteredDwells.length : 0,
      longest,
    };
  }, [filteredDwells, groups]);

  const durationBuckets = [
    { label: '< 15 min', max: 900, color: '#4caf50' },
    { label: '15–60 min', max: 3600, color: '#2196f3' },
    { label: '1–4 hr', max: 14400, color: '#ff9800' },
    { label: '4+ hr', max: Infinity, color: '#f44336' },
  ];
  const bucketCounts = durationBuckets.map((b, i) => ({
    ...b,
    n: filteredDwells.filter((d) => {
      const prev = durationBuckets[i - 1];
      return d.duration_seconds >= (prev ? prev.max : 0) && d.duration_seconds < b.max;
    }).length,
  }));
  const maxBucket = Math.max(1, ...bucketCounts.map((c) => c.n));

  const deviceCounts = uniqueDeviceTypes.map((t) => ({
    type: t,
    count: filteredDwells.filter((d) => d.device_type === t).length,
  }));
  const maxDevice = Math.max(1, ...deviceCounts.map((c) => c.count));

  // Zoom to a group: pick the longest-duration dwell in the cluster as the
  // representative target so the parent map flies to a meaningful point.
  // Reuses the existing onDwellClick contract to avoid plumbing a new prop.
  const handleGroupZoom = (group: DwellLocationGroup, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!group.dwells.length || !onDwellClick) return;
    const target = group.dwells.reduce(
      (best, d) => (d.duration_seconds > best.duration_seconds ? d : best),
      group.dwells[0],
    );
    setSelectedDwellId(target.id);
    onDwellClick(target);
  };

  const handleDwellClick = (dwell: DwellData) => {
    if (selectedDwellId === dwell.id) {
      setSelectedDwellId(null);
      onDwellClick?.(null);
    } else {
      setSelectedDwellId(dwell.id);
      onDwellClick?.(dwell);
    }
  };

  // Empty-data state (no dwells at all): show the shared empty state and bail.
  if (dwellLocations.length === 0) {
    return (
      <Box sx={{ p: 1.5 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LocationIcon sx={{ fontSize: 18 }} />
          Dwell Analysis
        </Typography>
        <EmptyState
          dense
          icon={<LocationIcon />}
          title="No dwell data yet"
          description='Click "Load" in the main panel to fetch dwell data for the current selection.'
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <LocationIcon sx={{ fontSize: 18 }} />
        Dwell Analysis
      </Typography>

      {/* Compact 5-tile stat row (DWELLS / LOCS / TOTAL / AVG / MAX) */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5, mb: 1 }}>
        {[
          { label: 'Dwells', value: stats.count.toLocaleString() },
          { label: 'Locs', value: String(stats.locations) },
          { label: 'Total', value: formatDuration(stats.totalDuration) },
          { label: 'Avg', value: formatDuration(stats.avg) },
          { label: 'Max', value: formatDuration(stats.longest) },
        ].map(({ label, value }) => (
          <Box
            key={label}
            sx={{
              py: 0.5,
              borderRadius: 0.5,
              bgcolor: 'rgba(255,255,255,0.04)',
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.5rem', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', lineHeight: 1 }}
            >
              {label}
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Breakdown (duration buckets + per-device counts) */}
      <Accordion
        defaultExpanded={false}
        sx={{
          mb: 0.75,
          '&::before': { display: 'none' },
          boxShadow: 'none',
          bgcolor: 'transparent',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '4px !important',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
          sx={{ minHeight: 28, '&.Mui-expanded': { minHeight: 28 }, py: 0, '& .MuiAccordionSummary-content': { my: 0.25 } }}
        >
          <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
            Breakdown
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 0.75, px: 1.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <Box>
              {bucketCounts.map((c) => (
                <Box key={c.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <Typography variant="caption" sx={{ width: 50, flexShrink: 0, fontSize: '0.55rem' }}>
                    {c.label}
                  </Typography>
                  <Box sx={{ flex: 1, height: 6, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                    <Box sx={{ width: `${(c.n / maxBucket) * 100}%`, height: '100%', bgcolor: c.color, borderRadius: 1 }} />
                  </Box>
                  <Typography variant="caption" sx={{ width: 24, textAlign: 'right', fontSize: '0.55rem', fontWeight: 600 }}>
                    {c.n}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box>
              {deviceCounts.map((c) => (
                <Box key={c.type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <Chip
                    label={c.type}
                    size="small"
                    color={DEVICE_COLORS[(c.type || '').toLowerCase()] ?? 'default'}
                    sx={{ fontSize: '0.5rem', height: 14, width: 50, '& .MuiChip-label': { px: 0.4 } }}
                  />
                  <Box sx={{ flex: 1, height: 6, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                    <Box sx={{ width: `${(c.count / maxDevice) * 100}%`, height: '100%', bgcolor: 'primary.main', borderRadius: 1 }} />
                  </Box>
                  <Typography variant="caption" sx={{ width: 24, textAlign: 'right', fontSize: '0.55rem', fontWeight: 600 }}>
                    {c.count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Filters (collapsed by default; shows an "Active" chip when filters are non-default) */}
      <Accordion
        defaultExpanded={false}
        sx={{
          mb: 0.75,
          '&::before': { display: 'none' },
          boxShadow: 'none',
          bgcolor: 'transparent',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '4px !important',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
          sx={{ minHeight: 28, '&.Mui-expanded': { minHeight: 28 }, py: 0, '& .MuiAccordionSummary-content': { my: 0.25 } }}
        >
          <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
            Filters
          </Typography>
          {(deviceFilter !== 'all' || minDurationMin > 0) && (
            <Chip
              label="Active"
              size="small"
              color="primary"
              sx={{ ml: 1, height: 14, fontSize: '0.5rem', '& .MuiChip-label': { px: 0.4 } }}
            />
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 0.75, px: 1.5 }}>
          <Stack spacing={1}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.7rem' }}>Device</InputLabel>
                <Select
                  value={deviceFilter}
                  label="Device"
                  onChange={(e) => setDeviceFilter(e.target.value)}
                  sx={{ fontSize: '0.7rem', '& .MuiSelect-select': { py: 0.5 } }}
                >
                  <MenuItem value="all">All</MenuItem>
                  {uniqueDeviceTypes.map((t) => (
                    <MenuItem key={t} value={t.toLowerCase()}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.7rem' }}>Sort</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort"
                  onChange={(e) => setSortBy(e.target.value as 'visits' | 'duration' | 'recent')}
                  sx={{ fontSize: '0.7rem', '& .MuiSelect-select': { py: 0.5 } }}
                >
                  <MenuItem value="visits">Most Visits</MenuItem>
                  <MenuItem value="duration">Duration</MenuItem>
                  <MenuItem value="recent">Recent</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ px: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem' }}>
                Min Duration: {minDurationMin === 0 ? 'None' : minDurationMin < 60 ? `${minDurationMin}m` : `${(minDurationMin / 60).toFixed(1)}h`}
              </Typography>
              <Slider
                value={minDurationMin}
                onChange={(_e, v) => setMinDurationMin(v as number)}
                min={0}
                max={720}
                step={5}
                marks={DURATION_MARKS}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => (v < 60 ? `${v}m` : `${(v / 60).toFixed(1)}h`)}
                size="small"
                sx={{ mt: 0, '& .MuiSlider-markLabel': { fontSize: '0.55rem' } }}
              />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Location groups */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
            Locations ({groups.length})
          </Typography>
          <Tooltip title={canLookupPlace ? 'Lookup place names for all locations' : 'Geocoder not configured'}>
            <span>
              <IconButton
                size="small"
                disabled={!canLookupPlace || lookupAllRunning || groups.length === 0}
                onClick={handleLookupAllPlaces}
                sx={{ p: 0.25 }}
                aria-label="Lookup place names for all locations"
              >
                {lookupAllRunning ? (
                  <CircularProgress size={14} thickness={5} />
                ) : (
                  <PlaceLookupIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box sx={{ maxHeight: 'calc(100vh - 380px)', overflow: 'auto' }}>
          {groups.map((group) => {
            const isExpanded = expandedGroup === group.key;
            const isRepeat = group.dwells.length > 1;
            const displayName = resolvedNames[group.key] || group.location_name;
            const isLookingUp = Boolean(lookupLoading[group.key]);
            const lookupErr = lookupError[group.key];
            const minDwellSec = group.dwells.reduce((m, d) => Math.min(m, d.duration_seconds), Infinity);
            const maxDwellSec = group.dwells.reduce((m, d) => Math.max(m, d.duration_seconds), 0);
            return (
              <Box
                key={group.key}
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: isExpanded ? 'primary.main' : 'divider',
                  bgcolor: isExpanded ? 'rgba(33, 150, 243, 0.04)' : 'transparent',
                  overflow: 'hidden',
                  transition: 'all 0.2s',
                }}
              >
                {/* Group header (clickable to expand/collapse) */}
                <Box
                  onClick={() => setExpandedGroup(isExpanded ? null : group.key)}
                  sx={{
                    p: 1,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.75,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <LocationIcon sx={{ fontSize: 16, mt: 0.25, color: 'error.main', flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={displayName}
                    >
                      {displayName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Chip
                        label={isRepeat ? `${group.dwells.length} visits · repeat` : `${group.dwells.length} visit`}
                        size="small"
                        color={isRepeat ? 'error' : 'default'}
                        variant={isRepeat ? 'filled' : 'outlined'}
                        sx={{
                          height: 18,
                          fontSize: '0.6rem',
                          fontWeight: isRepeat ? 700 : 400,
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                      <Chip
                        icon={<SpeedIcon sx={{ fontSize: '0.7rem !important' }} />}
                        label={formatDuration(group.total_duration)}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 }, '& .MuiChip-icon': { ml: 0.25 } }}
                      />
                      {group.place_type && (
                        <Chip
                          label={group.place_type}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ height: 18, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 }, textTransform: 'capitalize' }}
                        />
                      )}
                      {group.entities.length > 1 && (
                        <Chip
                          label={`${group.entities.length} entities`}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 } }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.6rem', mt: 0.25, display: 'block' }}
                    >
                      {formatDateShort(group.first_seen)} – {formatDateShort(group.last_seen)} · avg {formatDuration(group.avg_duration)}
                      {lookupErr ? ` · ${lookupErr}` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                    <Tooltip title={canLookupPlace ? 'Lookup place name' : 'Geocoder not configured'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!canLookupPlace || isLookingUp || lookupAllRunning}
                          onClick={(e) => handlePlaceLookup(group, e)}
                          sx={{ p: 0.25 }}
                          aria-label="Lookup place name"
                        >
                          {isLookingUp ? (
                            <CircularProgress size={14} thickness={5} />
                          ) : (
                            <PlaceLookupIcon sx={{ fontSize: 16, color: resolvedNames[group.key] ? 'success.main' : 'text.secondary' }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Zoom to location">
                      <IconButton size="small" onClick={(e) => handleGroupZoom(group, e)} sx={{ p: 0.25 }}>
                        <ZoomIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    {isExpanded ? <ArrowUpIcon sx={{ fontSize: 16 }} /> : <ArrowDownIcon sx={{ fontSize: 16 }} />}
                  </Box>
                </Box>

                {/* Expanded individual dwells inside this group */}
                <Collapse in={isExpanded}>
                  <Box sx={{ px: 1, pb: 1 }}>
                    <Box sx={{ mb: 1, px: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                          Duration range
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                          {formatDuration(minDwellSec === Infinity ? 0 : minDwellSec)} – {formatDuration(maxDwellSec)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={100}
                        sx={{
                          height: 3,
                          borderRadius: 1,
                          bgcolor: 'rgba(255,255,255,0.08)',
                          '& .MuiLinearProgress-bar': { bgcolor: 'error.main' },
                        }}
                      />
                    </Box>

                    {[...group.dwells]
                      .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime())
                      .map((dwell) => (
                        <Box
                          key={dwell.id}
                          onClick={() => handleDwellClick(dwell)}
                          sx={{
                            p: 0.75,
                            mb: 0.5,
                            borderRadius: 0.5,
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: selectedDwellId === dwell.id ? 'primary.main' : 'transparent',
                            bgcolor: selectedDwellId === dwell.id ? 'rgba(33, 150, 243, 0.1)' : 'rgba(255,255,255,0.02)',
                            '&:hover': { bgcolor: 'action.hover' },
                            transition: 'all 0.15s',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                                  {dwell.entity_id}
                                </Typography>
                                <Chip
                                  label={dwell.device_type}
                                  size="small"
                                  color={DEVICE_COLORS[(dwell.device_type || '').toLowerCase()] ?? 'default'}
                                  sx={{ fontSize: '0.55rem', height: 14, '& .MuiChip-label': { px: 0.5 } }}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                <TimeIcon sx={{ fontSize: 10, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                  {formatDateTime(dwell.start_time)}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                              <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                                {formatDuration(dwell.duration_seconds)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}

          {groups.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="caption" color="text.secondary">
                No dwells match the current filters
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DwellAnalysis;
